import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import { useLanguage } from "../../components/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Language } from "../../Types/types";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  postCategory,
  patchCategory,
  fetchCategoryById,
} from "../../components/Manual_List/fetch/slices";
import { uploadCategoryThumbnail } from "../../components/Manual_List/fetch/service";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import addcategorytranslation from "./TranslationAdd";
import { getBaseUrlWithoutApi } from "../../components/Manual_List/fetch/baseEndpoint";

const AddCategory: React.FC = () => {
  const { language } = useLanguage();
  const t = addcategorytranslation[language as Language];
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const category = useSelector((state: RootState) => state.data.category.data);

  const [names, setNames] = useState<Record<string, string>>({});
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchCategoryById({ id: parseInt(id), locale: language }));
    }
  }, [dispatch, id, language]);

  useEffect(() => {
    if (category && id) {
      if (typeof category.name === "object") {
        setNames(category.name as Record<string, string>);
      } else {
        console.error("Category name is not of expected type.");
      }
    }
  }, [category, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id && !thumbnail) {
      toast.error(t.thumbnailRequired);
      return;
    }

    const categoryData = {
      name: names,
    };

    let resultAction;
    if (id) {
      resultAction = await dispatch(
        patchCategory({ id: parseInt(id), categoryData })
      );
    } else {
      resultAction = await dispatch(postCategory(categoryData));
    }

    if (
      postCategory.fulfilled.match(resultAction) ||
      patchCategory.fulfilled.match(resultAction)
    ) {
      const categoryId = id ? parseInt(id) : resultAction.payload.id;

      if (thumbnail) {
        try {
          await uploadCategoryThumbnail(categoryId, thumbnail);
          toast.success(t.thumbnailSuccess);
        } catch (error) {
          console.error("Failed to upload thumbnail:", error);
          toast.error(t.thumbnailError);
        }
      }

      toast.success(t.categorySuccess);
      navigate("/categories-list");
    }
  };

  const handleNavigateToSubcategories = () => {
    navigate("/subcategories-list", {
      state: {
        names,
        subcategories: category?.subCategories || [],
        id,
      },
    });
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <ToastContainer />
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-category-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => navigate("/categories-list")}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft
                className="text-gray-600 dark:text-gray-400"
                size={20}
              />
            </button>
            <h1 className="text-2xl font-semibold">{t.title}</h1>
          </div>

          <div className="mb-6 flex justify-start gap-3">
            <button
              type="button"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
            >
              {t.details}
            </button>
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm dark:bg-white dark:text-black hover:bg-opacity-90 transition-all"
              onClick={handleNavigateToSubcategories}
            >
              {t.subcategories}
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t.name}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkBackground shadow-sm outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors"
                  value={names[language] || ""}
                  onChange={(e) =>
                    setNames({ ...names, [language]: e.target.value })
                  }
                  onClick={() => setIsNameModalOpen(true)}
                  autoComplete="off"
                  placeholder="Enter category name"
                />
                <Globe
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  size={20}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="thumbnail"
                className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t.thumbnail}
              </label>
              <input
                type="file"
                id="thumbnail"
                accept="image/*"
                required={!id}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkBackground shadow-sm outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple file:text-white hover:file:bg-opacity-90"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setThumbnail(e.target.files[0]);
                  }
                }}
              />
            </div>

            {id && (
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Thumbnail
                </label>
                <img
                  src={`${getBaseUrlWithoutApi()}/categories/${id}/icon.svg`}
                  alt="Category thumbnail"
                  className="size-16 bg-white rounded-lg shadow-md p-2"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-6">
            <button
              type="button"
              className="bg-gray-300 dark:bg-white dark:text-black text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              onClick={() => navigate("/categories-list")}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              form="add-category-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        translations={t}
        mode="name"
        values={names}
        setValues={setNames}
      />
    </div>
  );
};

export default AddCategory;
